import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronRightIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import useAuthorization from "@/hooks/useAuthorization";
import { HELP_CATEGORIES, helpArticles } from "./articles";
import type { HelpArticle, HelpCategoryId } from "./types";

interface HelpArticleListProps {
    onSelect: (article: HelpArticle) => void;
}

const HelpArticleList: React.FC<HelpArticleListProps> = ({ onSelect }) => {
    const { t } = useTranslation();
    const { hasRole } = useAuthorization();
    const [query, setQuery] = useState("");

    const visibleArticles = useMemo(() => {
        const isAdmin = hasRole("ROLE_ADMIN");
        return helpArticles.filter((article) => !article.adminOnly || isAdmin);
    }, [hasRole]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return visibleArticles;
        return visibleArticles.filter((article) => {
            if (t(article.titleKey).toLowerCase().includes(q)) return true;
            return article.sections.some(
                (s) =>
                    t(s.headingKey).toLowerCase().includes(q) ||
                    t(s.bodyKey).toLowerCase().includes(q)
            );
        });
    }, [visibleArticles, query, t]);

    const grouped = useMemo(() => {
        const map = new Map<HelpCategoryId, HelpArticle[]>();
        for (const article of filtered) {
            const list = map.get(article.category) ?? [];
            list.push(article);
            map.set(article.category, list);
        }
        return HELP_CATEGORIES.flatMap((cat) => {
            const list = map.get(cat);
            return list && list.length > 0 ? [{ category: cat, items: list }] : [];
        });
    }, [filtered]);

    return (
        <div className="flex flex-col h-full">
            <div className="px-5 pt-4 pb-3">
                <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={t("help.search.placeholder")}
                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                </div>
            </div>
            <div className="flex-1 overflow-y-auto px-5 pb-6">
                {grouped.length === 0 && (
                    <p className="text-sm text-gray-500 text-center mt-8">{t("help.search.empty")}</p>
                )}
                {grouped.map(({ category, items }) => (
                    <div key={category} className="mb-5">
                        <h3 className="text-sm font-semibold text-gray-700 mb-2">
                            {t(`help.categories.${category}`)}
                        </h3>
                        <ul className="space-y-1">
                            {items.map((article) => (
                                <li key={article.id}>
                                    <button
                                        type="button"
                                        onClick={() => onSelect(article)}
                                        className="w-full flex items-center justify-between text-left px-3 py-2 rounded-md border border-gray-200 hover:bg-gray-50 transition"
                                    >
                                        <span className="text-sm text-gray-800">{t(article.titleKey)}</span>
                                        <ChevronRightIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default HelpArticleList;
